const _ = require('lodash');
const BigNumber = require('bignumber.js');
const Cart = require('@/libraries/DigiTickets/Cart/Cart');
const CartLine = require('@/lib/Cart/CartLine');
const cartProductQuantityCalculator = require('@/libraries/DigiTickets/Cart/cartProductQuantityCalculator');
const ChangeCalculator = require('@/libraries/DigiTickets/Payment/ChangeCalculator');
const CustomerScreenStages = require('@/lib/CustomerScreen/CustomerScreenStages');
const EventNames = require('@/lib/Events/EventNames');
const ItemType = require('@/lib/Items/ItemType');
const LocalStorageKeys = require('@/lib/Storage/LocalStorageKeys');
const NetworkApiError = require('@/lib/Api/NetworkApiError');
const Payment = require('@/lib/Payments/Payment');
const PaymentCalculator = require('@/libraries/DigiTickets/Payment/PaymentCalculator');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const Ticket = require('@/lib/Tickets/Ticket');
const UserFacingError = require('@/libraries/DigiTickets/Errors/UserFacingError');
const { canMergeCarts, mergeCarts } = require('@/lib/Cart/mergeCarts');
const { cloneDeep } = require('@/functions/clone');
const { container } = require('@/container');
const { createCartItemGroupsFromItems } = require('@/libraries/DigiTickets/Cart/cartItemGroups');
const { getAllFieldInstancesFromCart, getFieldValuesFromCart } = require('@/lib/CustomFields/fieldFunctions');
const { ModalCancelledError } = require('@/vue/modals/ModalCancelledError');
const { removeOrphanedLines, updateChildLineReferences } = require('@/lib/Cart');
const { timeoutPromise } = require('@/functions/functions');
const { updateAngularUi } = require('@/functions/angular');

/**
 * @constructor
 *
 * !!! NOTE !!!
 * Before adding any additional properties here think about if they can be added to the new Cart model instead.
 * Even though using a Cart model is not fully implemented, the currentCart property is already in use and will
 * always be a Cart instance. It is reset to a new instance when the clear button is pressed.
 * You can access those properties as cartService.currentCart.x
 * The currentCart get stashed and restored as-is so if you add properties to Cart you don't need to worry about
 * adding them to StashedCart/StashedCartItem as well.
 *
 * @param {CartLineNumberFactory} cartLineNumberFactory
 * @param {CartLineSplitter} cartLineSplitter
 * @param {CartToOrderMapper} cartToOrderMapper
 * @param {CurrentDevice} currentDevice
 * @param {CustomerScreenDataService} customerScreenDataService
 * @param {DialogService} dialogService
 * @param {ExternalCartCalculator} externalCartCalculator
 * @param {EventBus} eventBus
 * @param {FieldInstanceFactory} fieldInstanceFactory
 * @param {KitchenOrderFactory} kitchenOrderFactory
 * @param {LocalStorageDataStore} localStorageDataStore
 * @param {NavigationService} navigationService
 * @param {OfferApplicator} offerApplicator
 * @param {OrderInProgressStasher} orderInProgressStasher
 * @param {PaymentMethodService} paymentMethodService
 * @param {PaymentService} paymentService
 * @param {ProductAvailabilityChecker} productAvailabilityChecker
 * @param {ReservationService} reservationService
 * @param {ThirdPartyRefGenerator} thirdPartyRefGenerator
 * @param {UserService} userService
 * @param {VuexService} vuexService
 */
const CartService = function (
    cartLineNumberFactory,
    cartLineSplitter,
    cartToOrderMapper,
    currentDevice,
    customerScreenDataService,
    dialogService,
    externalCartCalculator,
    eventBus,
    fieldInstanceFactory,
    kitchenOrderFactory,
    localStorageDataStore,
    navigationService,
    offerApplicator,
    orderInProgressStasher,
    paymentMethodService,
    paymentService,
    productAvailabilityChecker,
    reservationService,
    thirdPartyRefGenerator,
    userService,
    vuexService
) {
    this.cartLineNumberFactory = cartLineNumberFactory;
    this.cartLineSplitter = cartLineSplitter;
    this.cartToOrderMapper = cartToOrderMapper;
    this.currentDevice = currentDevice;
    this.customerScreenDataService = customerScreenDataService;
    this.dialogService = dialogService;
    this.externalCartCalculator = externalCartCalculator;
    this.eventBus = eventBus;
    this.fieldInstanceFactory = fieldInstanceFactory;
    this.kitchenOrderFactory = kitchenOrderFactory;
    this.localStorageDataStore = localStorageDataStore;
    this.navigationService = navigationService;
    this.offerApplicator = offerApplicator;
    this.orderInProgressStasher = orderInProgressStasher;
    this.paymentMethodService = paymentMethodService;
    this.paymentService = paymentService;
    this.productAvailabilityChecker = productAvailabilityChecker;
    this.reservationService = reservationService;
    this.thirdPartyRefGenerator = thirdPartyRefGenerator;
    this.userService = userService;
    this.vuexService = vuexService;

    /**
     * @type {?User}
     */
    this.user = null;

    /**
     * Flag to show if calculations are currently being run on the server.
     *
     * @type {boolean}
     */
    this.externalCalculationInProgress = false;

    this.calculator = new ChangeCalculator();

    /**
     * When the contents of the cart changes (items, customer account, etc.) this callback will be fired.
     *
     * @type {function|null}
     */
    this.onChangeFunction = null;

    /**
     * @type {CartItemGroup[]}
     */
    this.cartItemGroups = [];

    /**
     * This currentCart model contains all the data about the Cart that is currently being worked on.
     * (Almost) all data about a cart is now stored in this model instead of here in the CartService.
     *
     * @type {Cart}
     */
    this.currentCart = this.createEmptyCart();

    /**
     * The default lang key to use in the cart Pay Now button.
     *
     * @type {string}
     */
    this.payButtonLangKey = 'CART.PAY_NOW';

    this.init();
};

CartService.prototype = {
    /**
     * @param {User} user
     */
    setUser: function setUser(user) {
        this.user = user;
        this.currentCart.thirdPartyRef = this.thirdPartyRefGenerator.generateCartThirdPartyRef(this.currentCart);
        this.currentCart.thirdPartyID = this.currentCart.thirdPartyRef;
    },

    /**
     * Add a new CartLine to the current Cart.
     * This will now always add a new line instead of adjusting the quantity of an existing line.
     *
     * @param {CartLine} newCartItem
     *
     * @return {Promise<CartLine>}
     */
    async add(newCartItem) {
        if (this.currentCart.sellState.preventAdditional) {
            throw new UserFacingError(
                'Adding items to the cart is not currently allowed.',
                this.currentCart.sellState.preventAdditional
            );
        }

        newCartItem.lineNumber = this.cartLineNumberFactory.next();

        if (this.currentCart.giftAid) {
            // If giftAid is already enabled on the cart enable it on the item.
            newCartItem.enableGiftAid();
        } else if (this.currentCart.giftAidPrices) {
            newCartItem.useGiftAidRateDonation();
        } else if (this.currentCart.giftAid === false) {
            // If giftAid has been declined disable it on the item.
            // We are specifically checking for false because we don't want to disable Gift Aid if the question
            // has not been asked yet.
            newCartItem.disableGiftAid();
        }

        this.itemList.push(newCartItem);

        await this.recalculateAfterModifyingCartContents();

        return newCartItem;
    },

    /**
     * Either adds a new line to the cart, or updates the quantity of an existing line that matches the line provided.
     *
     * @param {CartLine} newCartLine
     * @param {boolean} triggeredByItemButton - was this action triggered by the item button in the right panel on the
     *     sell screen?
     *
     * @param {OptionSet[]} [optionSets]
     *
     * @returns {Promise<CartLine>} Returns either the supplied CartLine, or the existing line of the one that was
     *                              adjusted instead.
     */
    async addNewLineOrAdjustQuantityOfExisting(
        newCartLine,
        triggeredByItemButton = false,
        optionSets = []
    ) {
        // Check if there is an existing line that matches this one exactly (except qty)
        // already in the cart we can increase the qty of instead of adding another.
        let existingLine = this.currentCart.getLineByID(newCartLine.getID());

        // If there is an existing line that's the same, add to the quantity of that instead of inserting a new
        // line UNLESS the item button on the right was used and there are some ask early fields (item options).
        const shouldAddToExistingLine = !!existingLine
            && !(triggeredByItemButton && existingLine.itemInstances[0].fields.hasAskEarlyFields)
            && !(triggeredByItemButton && optionSets.length > 0);

        if (existingLine && shouldAddToExistingLine) {
            await this.adjustCartLineQuantity(existingLine, newCartLine.getQuantity());
            newCartLine = existingLine;
        } else {
            // Add a new line. Either there is no existing line for this item in the cart or we need to show the
            // itemEdit modal because there are askEarly custom fields for this item.
            await this.add(newCartLine);
        }

        this.checkProductAvailability(newCartLine);

        return newCartLine;
    },

    /**
     * Instead of calling CartLine.adjustQuantity directly, call this method instead and it will perform the necessary
     * calculations afterwards.
     *
     * @param {CartLine} cartLine
     * @param {number} adjustQuantityBy
     * @param {boolean} [removeFromCartIfNewQtyZero]
     *
     * @returns {Promise<void>}
     */
    async adjustCartLineQuantity(cartLine, adjustQuantityBy, removeFromCartIfNewQtyZero = true) {
        // This could check that the supplied cartLine belongs to the currentCart. But there are no bad
        // effects if you call it with a cartLine that isn't in the Cart, it just causes unnecessary recalculation.
        await this.adjustCartLineQuantityWithoutRecalculation(cartLine, adjustQuantityBy, removeFromCartIfNewQtyZero);
        await this.recalculateAfterModifyingCartContents();
    },

    /**
     * @private
     *
     * @param cartLine
     * @param adjustQuantityBy
     * @param removeFromCartIfNewQtyZero
     * @returns {Promise<unknown>}
     */
    async adjustCartLineQuantityWithoutRecalculation(cartLine, adjustQuantityBy, removeFromCartIfNewQtyZero = true) {
        cartLine.adjustQuantity(adjustQuantityBy);

        if (cartLine.getQuantity() === 0 && removeFromCartIfNewQtyZero) {
            await this.remove(cartLine);
        }
    },

    /**
     * Runs calculations for the currentCart to ensure its totals and other computed values are up-to-date.
     */
    calculate() {
        this.calculateCart(this.currentCart);
    },

    /**
     * Runs calculates for the given Cart to ensure its totals and other computed values are up-to-date.
     *
     * Note: Due to the historical non-distinction between the CartService (this class that performs cart-based
     * operations) and a Cart (an object that contains data about the current purchase) there are things called here
     * using 'this' (referring to CartService). Some data for the currently active cart is stored here in the service
     * instead of on the Cart model.
     *
     * Note: This method is currently only called from CartService.calculate() (see previous method) which calls it
     * with this.currentCart - however, although we may want to call this method using other carts in the future so
     * use the provided cart rather than using this.currentCart.
     *
     * @param {Cart} cart
     */
    calculateCart(cart) {
        // Update the active CartItemDiscounts on the lines.
        // See the comment on the applyManualLineOffers method for more info.
        // TODO: check if this can receive 'cart' rather than 'this' and refactor.
        this.offerApplicator.applyManualLineOffers(this);

        cart.totals.reset();

        // Remove any orphaned child lines.
        removeOrphanedLines(cart);

        updateChildLineReferences(cart);

        // Set correct amount of product quantities before stock level errors calculation.
        cart.cartProductQuantities = cartProductQuantityCalculator(this.itemList);

        // Calculate each line in the cart.
        for (let i = 0; i < cart.itemList.length; i++) {
            let item = cart.itemList[i];

            // Calculate each item option line at this point.
            for (let j = 0; j < item.optionLines.length; j++) {
                // Set the quantity of the option line to match the parent line.
                // This will also trigger a recalculation of the option line. We need that recalculation to not
                // trigger a recalculation of the cart again otherwise we'll have an infinite loop.
                item.optionLines[j].setQuantity(item.getQuantity(), false);
            }

            // Run calculate on the item but tell it not to calculate the cart, to prevent an infinite loop.
            item.calculate(false);

            cart.totals.addLine(item);

            // Check if there is sufficient stock for this line.
            // TODO: This will sort of just work for item options but we need to handle the UX for that.
            const availableStock = cart.getAvailableStockForLine(item);
            const requiredStock = cart.getRequiredStockForLine(item);
            item.hasStockLevelError = availableStock !== null && (requiredStock > availableStock);
        }

        this.calculator.setAmountDue(this.currentCart.getTotalDue());
        this.calculator.setAmountTendered(this.getTotalTendered());

        if (cart.isEditingOrder && cart.originalOrder) {
            let originalOrderTotal = new BigNumber(cart.originalOrder.total);
            let cartTotal = new BigNumber(this.currentCart.getTotal());
            cart.originalOrderDifference = cartTotal.minus(originalOrderTotal).toNumber();
        }

        this.calculateChange();
        this.payments = this.paymentService.cleanPayments(cart.payments);

        // TODO: This doesn't belong here because calculate() should be safe to call repeatedly with
        // no side effects. But updating the customer screen is a side effect.
        this.updateCustomerScreenCart();

        // Update UI.
        updateAngularUi();
    },

    /**
     * @private
     *
     * Call when the actual contents of the cart are changed
     * (items, sessions those items are for, and/or quantities).
     *
     * @returns {Promise<void>}
     */
    async itemsChanged() {
        // Update the modified at timestamp of the cart. This property can be watched to detect changes.
        // Here we use performance.now() to get a high resolution timestamp *since the page was loaded*.
        // Previously this was using Date.now() with a workaround in case the same milliseconds were encountered.
        this.currentCart.modified = performance.now();

        this.cartItemGroups = createCartItemGroupsFromItems(this.itemList);

        this.currentCart.regenerateContentHash();
        let newContentHash = this.currentCart.getContentHash();

        if (newContentHash !== this.currentCart.contentHash) {
            this.customerScreenDataService.setStage(CustomerScreenStages.SELL, CustomerScreenStages.COMPLETE);
        }

        this.currentCart.contentHash = newContentHash;
        await this.reloadFields();
    },

    /**
     * @param {{selectedOffers:Object<number, number>, selectedOfferCodes:Object<number, string>}} data
     *
     * @returns {Promise}
     */
    async setManualOfferData(data) {
        this.currentCart.selectedManualOfferIDs = data.selectedOffers;
        this.currentCart.selectedCouponCodeOfferCodes = data.selectedOfferCodes;
        this.currentCart.regenerateContentHash();
        return this.refreshExternalCalculations();
    },

    refreshExternalCalculations(force) {
        if (!this.debouncedRefreshExternalCalculations) {
            this.debouncedRefreshExternalCalculations = _.debounce(
                (resolve, reject, cart) => {
                    this.externalCartCalculator.recalculate(cart, [], force)
                        .then(resolve)
                        .catch(reject);
                },
                500
            );
        }
        return new Promise((resolve, reject) => {
            this.debouncedRefreshExternalCalculations(resolve, reject, this);
        });
    },

    /**
     * @param {CartLine} cartLine
     */
    checkProductAvailability(cartLine) {
        if (cartLine.getItemType() === ItemType.PRODUCT) {
            return this.productAvailabilityChecker.check(this.currentCart, this)
                .then((result) => {
                    this._triggerOnChange();

                    return result;
                });
        }
        return null;
    },

    /**
     * Remove all discounts from all the lines.
     */
    clearDiscounts: function clearDiscounts() {
        for (let i = 0; i < this.itemList.length; i++) {
            this.itemList[i].clearDiscounts();
        }
        this.calculate(false);
    },

    /**
     * Remove discounts that are really cost adjustments created by payments like tax on sale Experience vouchers.
     */
    clearPaymentDiscounts: function clearPaymentDiscounts() {
        for (let i = 0; i < this.itemList.length; i++) {
            this.itemList[i].clearPaymentDiscounts();
        }
    },

    calculateChange: function () {
        if (this.isRefund()) {
            return false;
        }

        // Payments can now be split over multiple payment lines. But we still need to keep track of change given.
        // Apply the change to any payments that support change (Cash, Flexi Voucher).

        let changeDue = this.calculator.getChange();
        let paymentsThatSupportChange = this.getPaymentsThatSupportChange();

        for (let i = 0; i < paymentsThatSupportChange.length; i++) {
            let payment = paymentsThatSupportChange[i];

            // This will also set the changeGiven to 0 where necessary.
            let changeAppliedToPayment = Math.min(changeDue, payment.tendered);
            payment.setChangeGiven(changeAppliedToPayment);

            changeDue -= changeAppliedToPayment;
        }

        if (changeDue > 0) {
            // If the operator has taken too much money by a non-cash payment method there will be more change
            // than we have taken in cash. However, the validation is strict about being able to receive more money than
            // the balance due so this block of code should only execute in a small number of circumstances such as when
            // we use a payment method that does not support change but that only exists in a small number of
            // denominations such as a Voucher.

            // For example Vouchers do not support change so if there is notional change we should not create a change
            // payment. e.g an order of £15 paid for with 2 x £10 vouchers -> £5 change
            let receivedPaymentsThatDoNotSupportChange = this.payments.filter((payment) => {
                // This may need to be reduced to only cover specific payment types (e.g. Voucher) but I think this is
                // correct.
                return !payment.paymentMethod.supportsChange;
            });

            // None of the payments in this order support change so we shouldn't create a change payment.
            if (receivedPaymentsThatDoNotSupportChange.length === this.payments.length) {
                console.log('There is excess change but none of the payment methods used support change.');
                return false;
            }

            // Create a new Cash payment method to hold this excess change.
            let cashPaymentMethod = this.paymentMethodService.getCashPaymentMethod();
            if (!cashPaymentMethod) {
                // TODO: How to handle this better?
                console.log('There is excess change but no cash payment method to apply it to.');
                return false;
            }

            let cashPayment = new Payment();
            cashPayment.setChangeGiven(changeDue);
            cashPayment.setPaymentMethod(cashPaymentMethod);
            this.currentCart.payments.push(cashPayment);
        }

        this._triggerOnChange();
        return true;
    },

    /**
     * @private
     *
     * Perform any cleanup necessary before binning the existing cart.
     * This method is concerned with not leaving any data or holds around that are no longer required.
     */
    async tearDownExistingCart() {
        if (this.currentCart) {
            // If there is a reservation with a token in the cart, delete it on the API.
            if (this.currentCart.reservation?.token) {
                // Not using the this.deleteReservation method because that will update the cart and persist it,
                // which we don't need to do here while destroying the cart.
                try {
                    await this.reservationService.deleteReservation(this.currentCart.reservation.token);
                } catch (e) {
                    // Failing to delete a reservation isn't the end of the world.
                    // And we might be offline, causing this to fail.
                    // The error will have already been logged. So do nothing else.
                }
            }
        }
    },

    /**
     * @private
     *
     * Reset the CartService state.
     */
    async reset() {
        this.calculator.clear();

        this.cartItemGroups = [];

        // Clear the selected payment method.
        this.setSelectedPaymentMethod(null);

        this.cartLineNumberFactory.reset();

        this.init();
    },

    /**
     * @returns {Cart}
     */
    createEmptyCart() {
        let cart = new Cart();

        // Set some flags on the new cart based on the previously saved values.
        // These will default to false if manageGroupBookings is not enabled.
        if (this.currentDevice.device.manageGroupBookings) {
            cart.groupBooking = !!this.localStorageDataStore.find(
                LocalStorageKeys.GROUP_BOOKING_SELECTED
            );
            cart.assignGroupBookingReference = !!this.localStorageDataStore.find(
                LocalStorageKeys.GROUP_BOOKING_ASSIGN_GROUP_REFERENCE_SELECTED
            );
        }

        return cart;
    },

    /**
     * Clear the current cart and set it to a new empty Cart.
     *
     * @returns {Promise}
     */
    async clear() {
        return this.setCurrentCart(this.createEmptyCart());
    },

    /**
     * Do not remove all the lines from the cart, but set the qty of all of them to zero.
     */
    async zeroQuantityAllLines() {
        await Promise.allSettled(
            this.currentCart.itemList.map((cartLine) => {
                if (!cartLine.isEditable()) {
                    return Promise.reject();
                }

                let reduceQtyBy = -cartLine.getQuantity();
                if (cartLine.minimumQuantity) {
                    reduceQtyBy += cartLine.minimumQuantity;
                }

                return this.adjustCartLineQuantityWithoutRecalculation(
                    cartLine,
                    reduceQtyBy,
                    false // Do not remove if the qty is zero.
                );
            })
        );
        await this.recalculateAfterModifyingCartContents();
    },

    init() {
        this.customerScreenDataService.setStage(CustomerScreenStages.WAIT, CustomerScreenStages.COMPLETE);
    },

    clearPayments: function clearPayments() {
        this.currentCart.payments = [];
        this._triggerOnChange();
    },

    /**
     * Update the cart on the customer facing screen.
     * Note that this method is called asynchronously, so there is the timeout before we update the customer screen
     * so that other changes should have time to be applied.
     */
    async updateCustomerScreenCart() {
        await timeoutPromise(100);

        const lastItem = this.getLastItemForCustomerScreen();
        if (!lastItem) {
            this.customerScreenDataService.setStage(CustomerScreenStages.WAIT, CustomerScreenStages.COMPLETE);
            return;
        }

        this.customerScreenDataService.updateState((state) => {
            state.cart = {
                lastItem: lastItem ? {
                    name: lastItem.getName(),
                    price: lastItem.getPrice(),
                    quantity: lastItem.getQuantity(),
                } : null,
                itemCount: this.itemList.length,
                totalQty: this.getTotalQty(),
                total: this.currentCart.getTotalDue(),
            };
        });
        this.customerScreenDataService.setStage(CustomerScreenStages.SELL, CustomerScreenStages.PAY);
    },

    /**
     * @return {Promise<Reservation>}
     */
    async createReservation() {
        const reservation = await this.reservationService.putReservationForCart(this.currentCart);

        await this.modifyCurrentCart((cart) => {
            cart.reservation = reservation;
        });

        return reservation;
    },

    /**
     * @return {Promise<void>}
     */
    async deleteReservation() {
        let clearCartReservation = true;
        try {
            if (this.currentCart.reservation?.token) {
                await this.reservationService.deleteReservation(this.currentCart.reservation.token);
            }
        } catch (err) {
            // The error could be that the reservation no longer exists (maybe it's expired).
            // In which case what we want to achieve is already done.

            // It could be that we got a network error. Perhaps the device is offline or having communication
            // issues. In this case, keep the reservation on the cart, as it may still be in place.
            if (err instanceof NetworkApiError) {
                console.warn('Reservation failed to delete due to network error. Keeping cart reseration.');
                clearCartReservation = false;
            }
        }

        if (clearCartReservation) {
            await this.modifyCurrentCart((cart) => {
                cart.reservation = null;
            });
        }
    },

    /**
     * @returns {CartLine[]}
     */
    getItems: function getItems() {
        return this.itemList;
    },

    /**
     * Returns an array of all offers (discounts) that have been applied to the items in the cart.
     * If the same offer was applied to multiple lines it will only be returned once (with the appropriate
     * quantity property)
     *
     * @returns {CartItemDiscount[]}
     */
    getDiscountSummary: function getDiscountSummary() {
        let discounts = {};
        let items = this.getItems();

        for (let i = 0; i < items.length; i++) {
            let item = items[i];

            for (let d = 0; d < item.discounts.length; d++) {
                let discount = item.discounts[d];
                let key = discount.offerID;
                if (discount.reason === PaymentMethodRef.GIFT_VOUCHER) {
                    key = discount.voucherCode;
                }
                if (discount && discount.isActive()) {
                    if (!discounts.hasOwnProperty(discount.offerID)) {
                        discounts[key] = _.clone(discount);
                        // Don't use the percentage as we may be discounting different items with the same
                        // offer, so it's hard and unnecessary to calculate.
                        discounts[key].percentage = 0;
                    } else {
                        discounts[key].qty += discount.qty;
                        discounts[key].amount += discount.amount;
                    }
                    // For VariableDiscounts the offer amount is always 0 and the discount amount can be
                    // different for each usage.
                    if (discount.isVariable()) {
                        discounts[key].amount += item.getTotalDiscountAmount(discount);
                    }
                }
            }
        }
        return Object.values(discounts);
    },

    /**
     * @returns {CartLine|null}
     */
    getLastItemForCustomerScreen() {
        // Return the last item in the itemList array that is not an Item Option.
        for (let i = this.currentCart.itemList.length - 1; i >= 0; i--) {
            let item = this.currentCart.itemList[i];
            if (item.getItemType() !== ItemType.ITEM_OPTION) {
                return item;
            }
        }

        return null;
    },

    /**
     * Returns the total number of items in the cart (sum of the qty per line)
     */
    getTotalQty: function getTotalQty() {
        let qty = 0;
        for (let i = 0; i < this.itemList.length; i++) {
            qty += this.itemList[i].getQuantity();
        }
        return qty;
    },

    /**
     * "Empty" means that there is nothing to do with the cart:
     * - There are no items
     * - There is no balance to pay
     * - There is no balance to refund
     *
     * @return {boolean}
     */
    isEmpty() {
        return this.itemList.length === 0
            && this.calculator.getAmountDue() === 0;
    },

    /**
     * Remove a line from the cart.
     *
     * @param {CartLine} itemToRemove
     */
    async remove(itemToRemove) {
        let index = this.itemList.indexOf(itemToRemove);
        if (index === -1) {
            return;
        }

        // This used to adjust the quantity of the line being removed down to 0 to put back session availability,
        // but we don't need to do that anymore as how session capacities are managed has changed.
        // We can go straight ahead and remove the line:
        await this.removeItemAtIndex(index);
    },

    /**
     * @private
     *
     * @param {number} index
     */
    async removeItemAtIndex(index) {
        this.itemList.splice(index, 1);

        // Disable Gift Aid if there are no longer any items with Gift Aid enabled in the cart.
        if (!this.currentCart.containsGiftAidableItems()) {
            this.clearGiftAidChoice();
        }
        await this.recalculateAfterModifyingCartContents();
    },

    /**
     * @return {boolean}
     */
    isBalancePaid() {
        return !!this.currentCart.balancePaid;
    },

    /**
     * @param {Payment} payment
     */
    addPayment: function (payment) {
        this.currentCart.payments.push(payment);
        // FIXME: using new Set won't work on objects.
        this.currentCart.payments = [...new Set(this.currentCart.payments)];
        this.calculate();
        this._triggerOnChange();
    },

    /**
     * Returns all payments in the cart that were made by a payment method that supports change (Cash, Flexi Voucher)
     * sorting by Cash first then amount.
     * This is because if there are both Cash and Flexi Voucher payments change should be issued as Cash.
     *
     * @returns {Payment[]}
     */
    getPaymentsThatSupportChange: function () {
        let cashPayments = this.currentCart.payments.filter(function (p) {
            return p.paymentMethod && p.paymentMethod.supportsChange;
        });

        return cashPayments.sort(function (a, b) {
            if (
                a.paymentMethod.ref === PaymentMethodRef.CASH
                && b.paymentMethod.ref !== PaymentMethodRef.CASH
            ) {
                return -1;
            }

            if (
                a.paymentMethod.ref !== PaymentMethodRef.CASH
                && b.paymentMethod.ref === PaymentMethodRef.CASH
            ) {
                return 1;
            }

            return a.tendered < b.tendered ? 1 : -1;
        });
    },

    /**
     * @deprecated - Use the getTotal() method of the Cart model instead.
     *
     * Get the total cost of the cart (after discounts).
     *
     * @return {number}
     */
    getTotal() {
        return this.currentCart.getTotal();
    },

    /**
     * @return {number}
     */
    getTotalExcludingOriginalOrder() {
        if (this.currentCart.isEditingOrder && this.currentCart.originalOrder) {
            let originalOrderTotal = new BigNumber(this.currentCart.originalOrder.total);
            let cartTotal = new BigNumber(this.currentCart.getTotal());
            return cartTotal.minus(originalOrderTotal).toNumber();
        }

        return this.currentCart.getTotal();
    },

    /**
     * @return {boolean}
     */
    isTakingBalancePayment() {
        return !!this.currentCart.sellState.balancePaymentAmount;
    },

    /**
     * @deprecated - Use the getTotalDue() method of the Cart model instead.
     *
     * Get the total cost of the cart (after discounts) taking into account any payments made for the original
     * order, **but not any new payments made for this cart**.
     *
     * @return {number}
     */
    getTotalDue() {
        return this.currentCart.getTotalDue();
    },

    getTotalTendered: function () {
        return PaymentCalculator.getTotalTendered(this.payments);
    },

    getTotalPaymentsThatSupportChangeTendered() {
        return PaymentCalculator.getTotalPaymentsThatSupportChangeTendered(this.payments);
    },

    /**
     * Sum of the 'tendered' from each payment on this order and the 'amount' from any payments on the original
     * order.
     *
     * @return {number}
     */
    getTotalPaid() {
        let total = new BigNumber(PaymentCalculator.getTotalTendered(this.payments));

        if (this.currentCart.originalOrder) {
            total = total.plus(new BigNumber(this.currentCart.originalOrder.getTotalPaid()));
        }

        return total.toNumber();
    },

    /**
     * @return {Payment[]}
     */
    getPayments: function getPayments() {
        return this.currentCart.payments;
    },

    /**
     * Returns how much is still due to be paid by (or refunded to) the customer.
     *
     * Note that this will not return a negative amount in the case of an overpayment.
     * (Or a positive amount in the case of an overrefund if the cart is a refund).
     * It will return a min (or max for refund) of zero.
     *
     * @return {number}
     */
    getRemainingBalance: function () {
        let totalDue = new BigNumber(this.currentCart.getTotalDue());

        if (this.currentCart.sellState.balancePaymentAmount) {
            totalDue = new BigNumber(this.currentCart.sellState.balancePaymentAmount);
        }

        let totalTendered = new BigNumber(this.getTotalTendered());

        let remainingBalance = totalDue.minus(totalTendered).toNumber();

        if (this.isRefund()) {
            return Math.min(0, remainingBalance);
        }

        return Math.max(0, remainingBalance);
    },

    isRefund: function isRefund() {
        return this.currentCart.getTotalDue() < 0;
    },

    /**
     * Returns the max amount that can be paid by a method other than Cash.
     * Return the remaining balance plus any cash already paid (as the cash can be returned as change).
     *
     * Returns the total amount payable if it is lower. This shouldn't happen, as the payment modal will submit
     * automatically once enough is received. But just in case.
     */
    getRemainingMaxPayment: function () {
        if (this.isRefund()) {
            return this.getRemainingBalance();
        }

        const remainingBalance = new BigNumber(this.getRemainingBalance());
        const totalPaymentsThatSupportChangeTendered = new BigNumber(this.getTotalPaymentsThatSupportChangeTendered());
        const balancePlusCashLikePayments = remainingBalance.plus(totalPaymentsThatSupportChangeTendered).toNumber();

        return Math.min(balancePlusCashLikePayments, this.currentCart.getTotalDue());
    },

    /**
     * @param {?PaymentMethod} paymentMethod
     */
    setSelectedPaymentMethod: function (paymentMethod) {
        const currentPaymentMethod = this.selectedPaymentMethod;

        if (
            (!currentPaymentMethod && !paymentMethod)
            || (currentPaymentMethod?.ref === paymentMethod?.ref)
        ) {
            // Switching to the same payment method. Do nothing.
            return;
        }

        // Set the selected payment method in the Vuex store.
        this.vuexService.getStore().commit('payment/setSelectedPaymentMethod', paymentMethod);

        this.eventBus.broadcast(
            EventNames.PAYMENT_METHOD_CHANGED,
            paymentMethod
        );

        // Trigger Angular UI update in case the event came from Vue.
        updateAngularUi();
    },

    /**
     * @returns {PaymentMethod|null}
     */
    get selectedPaymentMethod() {
        return this.vuexService.getStore().state.payment.selectedPaymentMethod;
    },

    /**
     * @return {?PaymentMethod}
     */
    getSelectedPaymentMethod: function () {
        return this.selectedPaymentMethod;
    },

    /**
     * @return {?string}
     */
    getSelectedPaymentMethodRef: function () {
        if (this.selectedPaymentMethod) {
            return this.selectedPaymentMethod.ref;
        }

        return null;
    },

    /**
     * @return {Order}
     */
    toOrder() {
        return this.cartToOrderMapper.map(this.currentCart);
    },

    /**
     * Populate the cart from the given StashedCart.
     *
     * @param {StashedCart} stashedCart
     *
     * @return {Promise<void>}
     */
    async populateFromStash(stashedCart) {
        await this.setCurrentCart(cloneDeep(stashedCart.currentCart) || this.createEmptyCart());

        // TODO: Run some revalidation on the cart. Check for past sessions and sessions with no
        // remaining capacity.

        return this.clean();
    },

    /**
     * Custom fields
     */

    /**
     * Create a new list of questions (fields) to ask and prune the answers (fieldData) to make sure everything is
     * in sync.
     *
     * @returns {Promise}
     */
    async reloadFields() {
        // const start = new Date().getTime();
        // Replace the field instances with the new ones.
        return this.fieldInstanceFactory.createFieldGroupsOnCart(this.currentCart)
            .then(() => {
                this._triggerOnChange();
            })
            .finally(() => {
                // const dur = new Date().getTime() - start;
                // console.debug(`reloadFields took ${dur}ms.`);
            });
    },

    /**
     * Checks if the last time the custom fields modal was opened the fields displayed were the same
     * as the current set of fields calculated for the cart.
     * Returns null if it was never opened. Otherwise returns a boolean.
     *
     * @return {Promise<boolean|null>}
     */
    async haveAllCustomFieldsBeenPresented() {
        if (this.currentCart.lastPresentedCustomFieldKeys === null) {
            return null;
        }

        // Build the collection of current fields.
        await this.reloadFields();

        // Get the keys of all the fields.
        let instanceKeys = getAllFieldInstancesFromCart(this.currentCart)
            .map((fieldInstance) => fieldInstance.getKey());

        // See if there are any new fields not already displayed.
        let diff = _.difference(instanceKeys, this.currentCart.lastPresentedCustomFieldKeys);

        return diff.length <= 0;
    },

    /**
     * Returns all the MembershipDatasets from all the lines in the cart.
     *
     * @returns {MembershipDataset[]}
     */
    getMembershipDatasets() {
        let datasets = [];
        for (let i = 0; i < this.itemList.length; i++) {
            if (this.itemList[i].isMembershipPlan()) {
                datasets = datasets.concat(this.itemList[i].membershipDatasets);
            }
        }
        return datasets;
    },

    /**
     * Enable Gift Aid for the order and increase item prices by their Gift Aid Rates.
     */
    enableGiftAid() {
        this.currentCart.giftAid = true;
        this.currentCart.giftAidPrices = true;

        // Enable gift aid on items.
        this.itemList.forEach((i) => i.enableGiftAid());
    },

    /**
     * Disable Gift Aid for the order but increase item prices by their Gift Aid Rates anyway.
     */
    enableGiftAidPricesOnly() {
        this.currentCart.giftAid = false;
        this.currentCart.giftAidPrices = true;

        // Disable gift aid on items.
        this.itemList.forEach((i) => i.disableGiftAid());
        // But also set the donation on items.
        this.itemList.forEach((i) => i.useGiftAidRateDonation());
    },

    /**
     * Disable Gift Aid for the order and undo any item price increases due to Gift Aid.
     */
    disableGiftAid() {
        this.currentCart.giftAid = false;
        this.currentCart.giftAidPrices = false;

        // Disable gift aid on items.
        this.itemList.forEach((i) => i.disableGiftAid());
    },

    /**
     * @return {?boolean}
     */
    giftAidEnabled: function () {
        return this.currentCart.giftAid;
    },

    /**
     * Reset the customers choice on Gift Aid.
     */
    clearGiftAidChoice: function () {
        // Disable GiftAid on items.
        this.disableGiftAid();

        // Then set the question back to unanswered.
        this.currentCart.giftAid = null;
        this.currentCart.giftAidPrices = null;
    },

    /**
     * This method is not currently used.
     *
     * @return {boolean}
     */
    containsDiscounts() {
        return !!this.itemList.find((i) => i.hasDiscounts());
    },

    /**
     * @return {boolean}
     */
    containsDiscountsExcludingExperienceVouchers() {
        return !!this.itemList.find((i) => i.hasDiscountsExcludingExperienceVouchers());
    },

    /**
     * Check if the cart contains any items that should be displayed on the kitchen screen.
     * (Disables the send to kitchen button in the hold modal if not).
     *
     * @return {boolean}
     */
    containsKitchenItems() {
        return this.currentCart.containsKitchenItems();
    },

    containsMemberships: function containsMemberships() {
        return this.currentCart.containsMemberships();
    },

    /**
     * Remove all memberships from the cart.
     *
     * @returns {null}
     */
    clearMemberships() {
        return this.currentCart.clearMemberships();
    },

    getCancellingMemberships: function getCancellingMemberships() {
        let memberships = [];

        for (let i = 0; i < this.itemList.length; i++) {
            if (this.itemList[i].cancellingMembership && this.itemList[i].cancellingMembership.isActive()) {
                memberships.push(this.itemList[i].cancellingMembership);
            }
        }

        return memberships;
    },

    /**
     * @returns {boolean}
     */
    allowPartialPayments() {
        // Allow partial payments for all non-recurring payments.
        return !this.currentCart.containsRepeatingPayments();
    },

    /**
     * @returns {?TransactionInProgress}
     */
    getTransactionInProgress() {
        return this.currentCart.transactionInProgress;
    },

    /**
     * Set the transactionInProgress property on the current cart.
     *
     * @param {TransactionInProgress} transactionInProgress
     *
     * @returns {Promise}
     */
    setTransactionInProgress(transactionInProgress) {
        return this.modifyCurrentCart((cart) => {
            cart.transactionInProgress = transactionInProgress;
        });
    },

    /**
     * Clear the transactionInProgress property on the current cart.
     *
     * @returns {Promise}
     */
    async clearTransactionInProgress() {
        if (this.currentCart && this.currentCart.transactionInProgress !== null) {
            console.log('Clear cart.transactionInProgress');
            this.vuexService.getStore().commit('payment/setRestrictToPaymentMethodRef', null);
            await this.modifyCurrentCart(async (cart) => {
                cart.transactionInProgress = null;
            });
        }
        console.log('Skipping clear cart.transactionInProgress');
    },

    /**
     * Generate a unique ID for a payment for this order.
     *
     * Format is:
     * {this.thirdPartyID}0{paymentAttemptNumber}
     * {companyID}0{deviceID}0{time}0{paymentAttemptNumber}
     *
     * Each time this is called it will increment this.paymentsAttempted so each ID returned is unique.
     *
     * @return {string}
     */
    generatePaymentThirdPartyID() {
        return this.thirdPartyRefGenerator.generatePaymentThirdPartyRef(this.currentCart);
    },

    /**
     * Generate a unique ID that is shorter than thirdPartyID.
     * This is formed by assembling integer string in the format {deviceID}0{time/1000}0{paymentAttemptNumber} that is
     * then base36 encoded to produce a string e.g. 2340156361680001 -> XBSU9YZEAB
     *
     * This was introduced to provide the externalReferenceId used by the Clover integration.
     *
     * Note: This does not increment this.paymentsAttempted it merely uses the current increment value.
     *
     * @return {string}
     */
    generateShortThirdPartyPaymentRef() {
        return this.thirdPartyRefGenerator.generateShortPaymentThirdPartyRef(this.currentCart);
    },

    /**
     * @param {string} reference
     */
    setStaffRef: function setStaffRef(reference) {
        this.currentCart.staffRef = reference;
        this._triggerOnChange();
    },

    /**
     * Register a callback to be fired when the cart content changes.
     * This callback function *should not do anything to modify the cart or its lines*.
     * This is purely an informational callback and should not be a hook to change things.
     *
     * TODO: This could be used to update the customer facing screen.
     *
     * @param {function} func
     */
    onChange: function onChange(func) {
        this.onChangeFunction = func;
    },

    /**
     * Remove any incomplete lines from the cart (things that can't be sold in their current state).
     *
     * The main use of this is to remove lines for new memberships that don't have any data entered.
     * This can happen if the user clicks to add a membership refreshes the page without confirming or cancelling
     * the membership datasets modal. The cart will be stashed with 1 qty for the membership but no data for that
     * membership.
     * We could check the length of the membershipDatasets array when restoring the cart and prevent adding it,
     * but this clean function has been added instead because in future we want to not need to fiddle with items
     * when stashing/unstashing. Instead this clean function can be called.
     */
    async clean() {
        for (let i = 0; i < this.currentCart.itemList.length; i++) {
            let line = this.currentCart.itemList[i];

            if (line.isMembershipPlan()) {
                if (line.quantity > 0 && !line.renewingMembership && !line.cancellingMembership) {
                    // Ensure the quantity of the line matches how much data has been entered.
                    let delta = line.membershipDatasets.length - line.quantity;
                    if (delta < 0) {
                        console.warn(`Adjusting qty of membership line by ${delta} to match data length.`);
                        await this.adjustCartLineQuantityWithoutRecalculation(line, delta);
                    }
                }
            }
        }
        await this.recalculateAfterModifyingCartContents();
    },

    /**
     * @private - Use modifyCurrentCart instead.
     *
     * Fire the cart contents changed callback.
     */
    _triggerOnChange: function _triggerOnChange() {
        if (typeof this.onChangeFunction === 'function') {
            this.onChangeFunction();
        }
    },

    /**
     * @param {Cart} cart
     */
    ensureCartHasThirdPartyRef(cart) {
        if (!cart.thirdPartyRef) {
            cart.thirdPartyRef = this.thirdPartyRefGenerator.generateCartThirdPartyRef(cart);
            cart.thirdPartyID = cart.thirdPartyRef;
        }
    },

    /**
     * Regenerate the thirdPartyRef/thirdPartyID for the current cart.
     * This gets called when the device changes, because the deviceID is part of the ref. The device changing
     * should only happen as part of the startup flow, so this should not affect any refs that have been sent
     * anywhere already.
     * The time part of the ref will remain the same when calling this, because the ref uses the createdAt
     * date from the cart to generate the ref.
     */
    regenerateCurrentCartThirdPartyRef() {
        this.currentCart.thirdPartyRef = this.thirdPartyRefGenerator.generateCartThirdPartyRef(this.currentCart);
        this.currentCart.thirdPartyID = this.currentCart.thirdPartyRef;
    },

    /**
     * @private
     *
     * Immediately replace the current cart with the supplied new cart.
     *
     * @param {Cart} cart
     */
    async setCurrentCart(cart) {
        // Do any necessary cleanup of an existing cart before we set the new one.
        await this.tearDownExistingCart();

        // Reset the CartService state.
        await this.reset();

        // Now set the new cart.
        this.currentCart = cart;

        this.ensureCartHasThirdPartyRef(cart);

        // Make sure the CartItems' cart properties point to this CartService instance.
        // TODO: Remove this coupling between them (PRO-972)
        this.currentCart.itemList.forEach((cartLine) => {
            cartLine.setCart(this);
        });

        // Set the highest lineNumber used by this cart so the next number produced will be 1 greater.
        // Math.max() returns 0 for an empty array.
        this.cartLineNumberFactory.setMin(
            Math.max(...cart.itemList.map((i) => i.lineNumber))
        );

        await this.recalculateAfterModifyingCartContents();
    },

    updateCurrentCartInVuex() {
        // We no longer store the whole cart in Vuex, only specific pieces of info.
        // In addition, there is a modified property, which Vue components can watch to know when the cart
        // has changed, and they should grab the latest data they want using cartService.currentCart from the container.

        const store = this.vuexService.getStore();
        store.commit('cart/setHasLines', this.currentCart ? this.currentCart.itemList.length > 0 : null);
        store.commit('cart/setIsEditingOrder', this.currentCart ? this.currentCart.isEditingOrder : null);
        store.commit('cart/setModified', this.currentCart ? this.currentCart.modified : null);
        store.commit('cart/setPaymentPattern', this.currentCart ? this.currentCart.getCurrentPaymentPattern() : null);
        store.commit('cart/setThirdPartyRef', this.currentCart ? this.currentCart.thirdPartyRef : null);
    },

    /**
     * @private
     *
     * Updates the copy of the current cart stored in the OrderInProgressStasher.
     */
    updateOrderInProgress() {
        this.orderInProgressStasher.setCart(this);
    },

    /**
     * Set the next cart to be used. When the user next opens the SellCtrl this cart will replace the current cart.
     * Replace the current cart with the supplied cart, in a user-friendly way.
     * If there are already items in the cart, ask the user if they want to merge the new cart with the existing
     * items, or clear the existing cart and use the new one.
     * This will return true if the user has confirmed that they want to use the new cart (either merging or replacing).
     *
     * The user can also cancel the operation, in which case the current cart will not be changed. And this will
     * return false.
     *
     * @param {Cart} newCart
     * @param {boolean} allowMerge If there already existing items in the cart, and the new cart is compatible, ask the
     *      user if they want to merge with the existing items? If this is false and the new cart is not compatible the
     *      user will only be given the option to clear the existing cart or cancel.
     *
     * @return {Promise<boolean>} Will return false if the user presses the cancel button on the merge cart question.
     */
    async setNewCart(newCart, allowMerge) {
        if (this.currentCart && this.currentCart.itemList.length > 0) {
            // There is already stuff in the cart.
            try {
                const mergeResult = await this.promptForReplacingExistingCart(newCart, allowMerge);
                if (mergeResult === 'keep') {
                    // The next cart shall be a merged version of the new cart and existing cart.
                    await this.setCurrentCart(mergeCarts(newCart, this.currentCart));
                } else if (mergeResult === 'clear') {
                    // Just set up to replace the existing cart.
                    // Set the new cart.
                    await this.setCurrentCart(newCart);
                }
            } catch (err) {
                if (!(err instanceof ModalCancelledError)) {
                    // Re-throw real errors.
                    throw err;
                }

                // Return false if the user cancelled.
                return false;
            }
        } else {
            await this.setCurrentCart(newCart);
        }

        return true;
    },

    /**
     * This method is designed to be called from anywhere in the app that is not the Sell page.
     * It allows adding a ticket to the cart without being on the Sell page. The difficulty with that is that
     * event based tickets need to show the session picker.
     *
     * @param {AbstractItem|Ticket} ticket
     *
     * @return {Promise<boolean>} Will return false if the user presses the cancel button on the merge cart question.
     */
    async addItemToCartAndGoToSell(ticket) {
        const cart = this.createEmptyCart();

        if (!ticket.eventID) {
            // Open Ticket - just add it to the cart.
            const cartLine = new CartLine(this, ticket, this.userService.currentItemBranch);
            cartLine.setQuantity(1);
            cart.addItem(cartLine);
        }
        // For event tickets we set the next cart to an empty cart so that the merge prompt is triggered
        // if required. After that we will redirect to the Sell page and pop up the session picker to actually
        // add the ticket.

        // The setNewCart method handles prompting if the user wants to merge with or replace any items
        // that are already in the cart.

        const cartSet = await this.setNewCart(cart, true);
        if (!cartSet) {
            return false;
        }

        if (ticket.eventID) {
            await this.modifyCurrentCart((currentCart) => {
                // Set up the sell state to auto-start the flow for adding this event ticket
                // when the sell view shows.
                currentCart.sellState.autoAddEventTicketID = ticket.ID;
            });
        }

        this.navigationService.viewSell();

        return true;
    },

    /**
     * @private
     *
     * Show a dialog to the user saying there are already items in the cart. Ask if they want to bin the existing cart
     * and use only the new cart, or merge the new cart with the existing cart.
     *
     * @param {Cart} newCart
     * @param {boolean} allowMerge If it's possible, allow the user to select to merge the carts?
     *
     * @return {Promise<string>} Returns a promise with a string value:
     *      clear - if they want to bin the existing cart and only use the new cart.
     *      keep - if the user wants to merge the next cart with the existing cart
     *      Rejects if the user presses cancel.
     */
    promptForReplacingExistingCart(newCart, allowMerge) {
        let promptStrings = [];
        let customBtns = [];
        const canMerge = canMergeCarts(this.currentCart, newCart, 'MERGE_CART');

        const keepButton = {
            label: 'ADD_TO_NON_EMPTY_CART.ADD_TO_EXISTING_BTN',
            color: 'success',
            value: 'keep',
        };
        const clearButton = {
            label: 'ADD_TO_NON_EMPTY_CART.CLEAR_CART_BTN',
            color: 'warning',
            value: 'clear',
        };

        if (allowMerge && canMerge === true) {
            // Merging is allowed and possible. Show both the keep and clear buttons.
            promptStrings = [
                'ADD_TO_NON_EMPTY_CART.PROMPT_EXISTING_ITEMS',
                'ADD_TO_NON_EMPTY_CART.PROMPT_MERGE_QUESTION',
            ];
            customBtns = [keepButton, clearButton];
        } else if (allowMerge) {
            // Merging is allowed but not possible. Only show the clear option, but also say why it can't be merged.
            promptStrings = [
                'ADD_TO_NON_EMPTY_CART.PROMPT_EXISTING_ITEMS',
            ];

            if (canMerge instanceof UserFacingError) {
                promptStrings.push(canMerge.getLangKey());
            } else if (allowMerge) {
                // Show a generic reason why the carts can't be merged.
                promptStrings.push('ADD_TO_NON_EMPTY_CART.PROMPT_CANNOT_MERGE');
            }

            promptStrings.push('ADD_TO_NON_EMPTY_CART.PROMPT_CONFIRM_CLEAR');

            customBtns = [clearButton];
        } else {
            // Merging is never allowed from this flow. Only show the clear option.
            promptStrings = [
                'ADD_TO_NON_EMPTY_CART.PROMPT_EXISTING_ITEMS',
                'ADD_TO_NON_EMPTY_CART.PROMPT_CONFIRM_CLEAR',
            ];
            customBtns = [clearButton];
        }

        return this.dialogService.customConfirm(
            promptStrings,
            customBtns,
        );
    },

    /**
     * The below setters and getters are for backward compatibility.
     * These used to be properties of CartService but are now properties of the Cart model (at this.currentCart)
     */

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {Customer}
     */
    get customer() {
        return this.currentCart.customer;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {Customer} customer
     */
    set customer(customer) {
        this.currentCart.customer = customer;
    },

    /**
     * Returns all the field data combined (both order level from the Cart, and item level from the ItemInstances).
     * The same as the.fieldData held in the past.
     */
    get fieldData() {
        return getFieldValuesFromCart(this.currentCart);
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {CartLine[]}
     */
    get itemList() {
        return this.currentCart.itemList;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {CartLine[]} itemList
     */
    set itemList(itemList) {
        this.currentCart.itemList = itemList;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {?Order}
     */
    get originalOrder() {
        return this.currentCart.originalOrder;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {?Order} order
     */
    set originalOrder(order) {
        this.currentCart.originalOrder = order;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {?Reservation}
     */
    get reservation() {
        return this.currentCart.reservation;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {?Reservation} reservation
     */
    set reservation(reservation) {
        this.currentCart.reservation = reservation;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {?boolean}
     */
    get giftAid() {
        return this.currentCart.giftAid;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {?boolean} giftAid
     */
    set giftAid(giftAid) {
        this.currentCart.giftAid = giftAid;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {?boolean}
     */
    get giftAidPrices() {
        return this.currentCart.giftAidPrices;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {?boolean} giftAidPrices
     */
    set giftAidPrices(giftAidPrices) {
        this.currentCart.giftAidPrices = giftAidPrices;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {boolean}
     */
    get ignoreDuplicateRef() {
        return this.currentCart.ignoreDuplicateRef;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {boolean} ignoreDuplicateRef
     */
    set ignoreDuplicateRef(ignoreDuplicateRef) {
        this.currentCart.ignoreDuplicateRef = ignoreDuplicateRef;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {?string}
     */
    get notes() {
        return this.currentCart.notes;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {?string} notes
     */
    set notes(notes) {
        this.currentCart.notes = notes;
        this._triggerOnChange();
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {Payment[]}
     */
    get payments() {
        return this.currentCart.payments;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {Payment[]} payments
     */
    set payments(payments) {
        this.currentCart.payments = payments;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {number}
     */
    get paymentsAttempted() {
        return this.currentCart.paymentsAttempted;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {number} paymentsAttempted
     */
    set paymentsAttempted(paymentsAttempted) {
        this.currentCart.paymentsAttempted = paymentsAttempted;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {Object<number, number>}
     */
    get selectedManualOfferIDs() {
        return this.currentCart.selectedManualOfferIDs;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {Object<number, number>} selectedManualOfferIDs
     */
    set selectedManualOfferIDs(selectedManualOfferIDs) {
        this.currentCart.selectedManualOfferIDs = selectedManualOfferIDs;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {Object<number, string>}
     */
    get selectedCouponCodeOfferCodes() {
        return this.currentCart.selectedCouponCodeOfferCodes;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {Object<number, string>} selectedCouponCodeOfferCodes
     */
    set selectedCouponCodeOfferCodes(selectedCouponCodeOfferCodes) {
        this.currentCart.selectedCouponCodeOfferCodes = selectedCouponCodeOfferCodes;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {?string}
     */
    get staffRef() {
        return this.currentCart.staffRef;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {?string} staffRef
     */
    set staffRef(staffRef) {
        this.currentCart.staffRef = staffRef;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {?string}
     */
    get thirdPartyID() {
        return this.currentCart.thirdPartyID;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {?string} thirdPartyID
     */
    set thirdPartyID(thirdPartyID) {
        this.currentCart.thirdPartyID = thirdPartyID;
    },

    /**
     * @deprecated - Access via this.currentCart instead.
     *
     * @return {?string}
     */
    get thirdPartyRef() {
        return this.currentCart.thirdPartyRef;
    },

    /**
     * @deprecated - Access via this.currentCart instead. Ideally, use this.modifyCurrentCart().
     *
     * @param {?string} thirdPartyRef
     */
    set thirdPartyRef(thirdPartyRef) {
        this.currentCart.thirdPartyRef = thirdPartyRef;
    },

    /**
     * Return the quantity of a session on all tickets in the cart excluding instances that have already been bought
     * because otherwise we end up double counting - we don't need to check the availablity of places that we already
     * have!
     *
     * @param {number} sessionID
     *
     * @return {number}
     */
    getCartSessionRequiredSpacesQuantity(sessionID) {
        let total = 0;
        this.itemList.forEach((item) => {
            if (item.session && item.session.ID === sessionID) {
                let ticket = Object.assign(new Ticket(), item.item);
                // Ignore tickets that have already been purchased.
                total += (item.quantity * ticket.people) - (item.qtyBeforeAdjustment * ticket.people);
            }
        });
        return total;
    },

    /**
     * Call this function after performing some function that modifies the contents of the cart (the items / qty).
     * It will run all the necessary calculations after the Cart and/or its lines have been modified.
     *
     * @returns {Promise<void>}
     */
    async recalculateAfterModifyingCartContents() {
        await this.calculate();
        await this.itemsChanged();
        await this._triggerOnChange();
        this.recalculatePayButtonLangKey();
        this.updateCurrentCartInVuex();

        // TODO: This is not called yet but should be used in PROP-2009 instead of the onChange function.
        // this.updateOrderInProgress();

        updateAngularUi();
    },

    /**
     * As the state of the cart changes ensure that the pay button displays the required text.
     */
    recalculatePayButtonLangKey() {
        if (this.isRefund()) {
            this.payButtonLangKey = 'CART.REFUND_NOW';
            return;
        }

        if (!this.isEmpty() && !this.isRefund() && this.getRemainingBalance() === 0) {
            this.payButtonLangKey = 'CART.SAVE_NOW';
            return;
        }

        // The default state.
        this.payButtonLangKey = 'CART.PAY_NOW';
    },

    /**
     * @param {?number} orderID
     * @param {?Order} [order]
     *
     * @returns {Promise}
     */
    async setUnconfirmedOrder(orderID, order) {
        return this.modifyCurrentCart((cart) => {
            cart.setUnconfirmedOrderID(orderID);
            cart.unconfirmedOrder = order || null;
        });
    },

    /**
     * @returns {Promise<Order>}
     */
    async loadUnconfirmedOrder() {
        if (!this.currentCart.unconfirmedOrderID) {
            throw new Error('An unconfirmed order has not been created for the current cart.');
        }

        // Load the full unconfirmed order if we don't already have it, or if it is the wrong order.
        if (
            !this.currentCart.unconfirmedOrder
            || this.currentCart.unconfirmedOrder.ID !== this.currentCart.unconfirmedOrderID
        ) {
            const order = await container.orderSearcher.remoteGet(this.currentCart.unconfirmedOrderID);

            await this.setUnconfirmedOrder(order.ID, order);
        }

        return this.currentCart.unconfirmedOrder;
    },

    /**
     * If you want to modify something in the current cart and ensure those changes are persisted to local storage,
     * and the cart is correctly recalculated after, use this method.
     * Example:
     * await cartService.modifyCurrentCart(async (cart) => {
     *     cart.unconfirmedOrderID = 123;
     * });
     *
     * Returns any value returned by the function passed in.
     *
     * @param {function(Cart)} func
     *
     * @returns {Promise}
     */
    async modifyCurrentCart(func) {
        const result = await func(this.currentCart);
        await this.recalculateAfterModifyingCartContents();
        return result;
    },

    /**
     * @param {string} key
     *
     * @returns {?CartItemGroup}
     */
    getCartItemGroupByKey(key) {
        return this.cartItemGroups.find((group) => group.key === key) || null;
    },

    /**
     * @param {boolean} value
     * @param {boolean} persist
     */
    async setGroupBooking(value, persist = false) {
        await this.modifyCurrentCart((cart) => {
            cart.groupBooking = value;
        });

        // Remember the new value in local storage.
        // This should only be done if it is set based on a user interacting with the toggle.
        // Otherwise, we create a positive feedback loop when the toggle is set to true.
        if (persist) {
            this.localStorageDataStore.persist(LocalStorageKeys.GROUP_BOOKING_SELECTED, value);
        }
    },

    async setAssignGroupBookingReference(value, persist = false) {
        await this.modifyCurrentCart((cart) => {
            cart.assignGroupBookingReference = value;
        });

        // Remember the new value in local storage.
        // This should only be done if it is set based on a user interacting with the toggle.
        // Otherwise, we create a positive feedback loop when the toggle is set to true.
        if (persist) {
            this.localStorageDataStore.persist(
                LocalStorageKeys.GROUP_BOOKING_ASSIGN_GROUP_REFERENCE_SELECTED,
                value
            );
        }
    },

    async updateGroupBookingReference(originalReference, newReference) {
        this.currentCart.itemList.forEach((cartLine) => {
            if (cartLine.groupBookingReference === originalReference) {
                cartLine.groupBookingReference = newReference;
            }
        });

        await this.recalculateAfterModifyingCartContents();
    },

    async resolveOrderSaveErrors() {
        return this.modifyCurrentCart((cart) => {
            cart.getUnresolvedOrderSaveErrors().forEach((e) => {
                e.resolved = true;
            });
        });
    },

    async resolvePaymentSaveErrors() {
        return this.modifyCurrentCart((cart) => {
            cart.getUnresolvedPaymentSaveErrors().forEach((e) => {
                e.resolved = true;
            });
        });
    },
};

module.exports = CartService;
